import classNames from 'classnames'
import { type ReactNode, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, Icon, Typography } from 'shared/ui'
import { formatPhoneNumberToInternational } from 'shared/lib'
import { ModalInfoBlockWithTitle } from 'shared/ui/Modal/ui/ModalInfoBlockWithTitle/ModalInfoBlockWithTitle'
import { type IModalInfoRowProps } from 'shared/ui/Modal/ui/ModalInfoRow/ModalInfoRow'
import { openIntercom } from 'entities/Intercom'
import { ModalHeader } from 'pages/settings/pages/compliance/ui/ModalHeader/ModalHeader'
import { type InternationalPageStore } from 'pages/settings/pages/compliance/pages/international'
import styles from './styles.module.scss'

type IInternationalBusinessProfileModalContentProps = {
  editBusinessProfile: () => void
  statusData: ReactNode
  closeModal: () => void
  internationalPageStore: InternationalPageStore
}

export const InternationalBusinessProfileModalContent: FC<IInternationalBusinessProfileModalContentProps> =
  observer(({ statusData, closeModal, editBusinessProfile, internationalPageStore }) => {
    const {
      internationalRegulation,
      getRegistrationAuthorityCountries,
      getRegistrationAuthorityInfo,
    } = internationalPageStore

    if (!internationalRegulation?.businessData) return null
    if (!internationalRegulation?.addressData) return null
    if (!internationalRegulation?.authorizedRepresentativeData) return null

    const {
      legalBusinessName,
      businessRegistrationNumber,
      registrationAuthority,
      businessWebsiteUrl,
    } = internationalRegulation.businessData

    const { businessAddress, country } = internationalRegulation.addressData

    const { businessEmail, lastName, firstName, phoneNumber } =
      internationalRegulation.authorizedRepresentativeData

    const errorMessage = internationalRegulation.registrationErrors

    const registrationAuthorityData = getRegistrationAuthorityCountries(registrationAuthority)
    const info = getRegistrationAuthorityInfo(registrationAuthority)

    const generalInfoLines: IModalInfoRowProps[] = [
      { label: 'Legal business name', value: legalBusinessName },
      {
        label: 'Registration authority',
        value: registrationAuthorityData ? (
          <div className={styles.field}>
            <Icon icon={registrationAuthorityData.icon} fontSize={16} />
            <Typography
              variant='text-input-field-r'
              color='var(--content-primary-primary)'
              ariaLabel='Registration_authority_value'
            >
              {registrationAuthorityData.label}
            </Typography>
          </div>
        ) : (
          '-'
        ),
      },
      {
        label: info?.label,
        value: businessRegistrationNumber,
      },
      {
        label: 'Business website URL',
        value: businessWebsiteUrl ? <a href={businessWebsiteUrl}>{businessWebsiteUrl}</a> : '-',
      },
    ]

    const businessAddressLines: IModalInfoRowProps[] = [
      { label: 'Country', value: country },
      {
        label: 'Business address',
        value: (
          <div className={styles.address}>
            <Typography
              variant='text-input-field-r'
              color='var(--content-primary-primary)'
              ariaLabel='Business_address_value_Line1'
            >
              {businessAddress}
            </Typography>
            <Typography
              ariaLabel='Business_address_value_Line2'
              variant='text-input-field-r'
              color='var(--content-primary-primary)'
            >
              {internationalRegulation.secondLine}
            </Typography>
          </div>
        ),
      },
    ]

    const authorizedRepresentativeLines: IModalInfoRowProps[] = [
      { label: 'Name', value: `${firstName} ${lastName}` },
      { label: 'Business email', value: businessEmail },
      { label: 'Phone number', value: formatPhoneNumberToInternational(phoneNumber) },
    ]

    return (
      <div className={styles.wrap}>
        <ModalHeader header={legalBusinessName} statusData={statusData} />

        <div
          className={classNames({
            [styles.topBorder]: !errorMessage,
          })}
        >
          {errorMessage && (
            <Alert
              item={{
                title: 'Here goes regulatory bundle rejection reason',
                type: 'error',
                desc: errorMessage,
              }}
              actions={[
                {
                  variant: 'button',
                  type: 'primary',
                  text: 'Update',
                  onClick: () => {
                    editBusinessProfile()
                    closeModal()
                  },
                },
                {
                  variant: 'button',
                  type: 'secondary',
                  text: 'Learn more',
                  onClick: () => {
                    openIntercom()
                    closeModal()
                  },
                },
              ]}
            />
          )}

          <ModalInfoBlockWithTitle title='General info' lines={generalInfoLines} />
          <ModalInfoBlockWithTitle title='Business address' lines={businessAddressLines} />
          <ModalInfoBlockWithTitle
            title='Authorized representative'
            lines={authorizedRepresentativeLines}
          />
        </div>
      </div>
    )
  })
